import React from 'react';
import { useUniqueId } from '../useUniqueId';
import classNames from 'classnames';
import { ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import { useTPAComponentsConfig } from '../TPAComponentsProvider';
import styles from './FloatingDropdown.module.scss';
/** `DATA_HOOKS` in FloatingDropdown/constants.js. */
const FD_HOOKS = {
    base: 'floating-dropdown-base',
    baseSelectedValue: 'floating-dropdown-base-selected-value',
    coreDropdown: 'core-dropdown',
    nativeSelect: 'floating-dropdown-native-select',
    floatingDropdownButton: 'floating-dropdown-button',
};
/** `DATA_HOOKS` in CoreDropdownContent.js and CorePopover's const.js. */
const CORE_HOOKS = {
    dropdownContent: 'dropdown-content',
    optionsContainer: 'dropdown-content-options-container',
    dropdownContentOption: 'dropdown-content-option',
    popoverElement: 'popover-element',
    popoverContent: 'popover-content',
};
/** `DATA_HOOKS.option` in Dropdown/constants.js. */
const DROPDOWN_OPTION_HOOK = 'dropdown-option';
/** `createOptionId` / `createOptionLabelId` in Dropdown/helpers.js. */
const createOptionId = (containerId, optionId) => `${containerId}_option-${optionId}`;
const createOptionLabelId = (id) => `${id}-text`;
/**
 * Drop-in replacement for `FloatingDropdown` from `wix-ui-tpa/cssVars`.
 *
 * Upstream this is ELEVEN stylesheets over a nine-level DOM (FloatingDropdown -> CoreDropdown ->
 * CorePopover -> FloatingDropdownBase -> TextButton -> CoreButtonNext, and, in the popover,
 * CoreDropdownContent -> CoreDropdownOption -> DropdownOption -> Text). The stylesheet ported
 * alongside this file is the composition of all eleven, so the DOM has to match it. What WUT
 * renders on desktop is:
 *
 *   div.root[.displayInline][data-mobile]                          <- FloatingDropdown
 *     div.popoverRoot.coreDropdownRoot.dropdown[.contentVisible]   <- CoreDropdown over CorePopover
 *       div.popoverElement                                          <- the click target
 *         div.fdBaseRoot.floatingDropdownBase                       <- FloatingDropdownBase
 *           button.textButtonRoot.tbPriority-secondary.coreBtnRoot.btn
 *             span.btnContent
 *               div.fdContent
 *                 div.textContent > span.label + div.selectedValue
 *                 svg.arrowIcon
 *       div.popover.dropdownContainer.popoverContent                <- only while open
 *         div                                                       <- CorePopover's content div
 *           div.coreContentRoot.coreDropdownContent
 *             div.optionsContainer[role=menu]
 *               div.coreOptionRoot.coreContentDropdownOption[role=menuitem]
 *                 div.optionRoot.option
 *                   div.optionContentWrapper
 *                     span.textRoot.textTypography-runningText.title
 *
 * On mobile the popover is gone entirely and the base renders a native `<select>` over the same
 * content — which is what `sorting-selector.po.ts` drives through `[data-hook~=
 * floating-dropdown-native-select]`, so that select must NOT exist on desktop.
 *
 * The `data-part` attributes are kept on the elements whose WUT pseudo-element they stand for,
 * because comments-sort.scss styles `::floatingDropdownBase`, `::btn`, `::label`,
 * `::selectedValue`, `::textContent`, `::arrowIcon`, `::dropdown`, `::dropdownContent`,
 * `::option` and `::option::title` through them from another CSS module.
 *
 * One deviation, and the only one: upstream positions the popover with react-popper. Here the
 * same `bottom-start` result is produced by the inline style below — absolute, and translated
 * down by the trigger's height with popper's `-1px` horizontal nudge, which is exactly what
 * `.dropdownContainer`'s `margin-left: 1px` is written to cancel (its upstream comment says so).
 */
export const FloatingDropdown = ({ className, label, value, options, onChange, mobile: mobileProp, forceContentElementVisibility = false, 'aria-label': ariaLabel, 'data-hook': dataHook, }) => {
    /*
     * `mobile` / `rtl` come from TPAComponentsContext when a caller does not pass them.
     *
     * `@wix/yoshi-flow-editor` wraps every widget in WUT's own `TPAComponentsProvider` (see
     * `initProviders.js`), so upstream components always had these; a port that takes them only
     * as props stays permanently on its desktop/LTR branch. An explicit prop still wins.
     */
    const tpaConfig = useTPAComponentsConfig();
    const mobile = mobileProp ?? tpaConfig.mobile ?? false;
    const uid = useUniqueId('floating-dropdown-options-container_');
    const containerId = uid;
    const [isExpanded, setIsExpanded] = React.useState(false);
    /* `shown` in CoreDropdown.render: mounted when forced, or when open with something to show.
       Upstream also suppresses it while `disabled`; that prop is gone, since the sort selector — the
       only consumer — is never disabled. */
    const shown = forceContentElementVisibility || (isExpanded && options.length > 0);
    const rootRef = React.useRef(null);
    const elementRef = React.useRef(null);
    const selectableOptions = options.filter((option) => !option.divider);
    const selectedOption = options.find((option) => option.id === value);
    /* `hoveredIndex` in CoreDropdownContent: it starts on the selected option and follows the
       pointer, and it is what `.coreOptionHovered` keys off. */
    const selectedIndex = selectableOptions.findIndex((option) => option.id === value);
    const [hoveredIndex, setHoveredIndex] = React.useState(-1);
    /*
     * Upstream keeps hover and keyboard focus apart: `DropdownOption`'s `isFocused` comes from
     * `focusedOptionId` (arrow keys), and only that state draws the focus ring. This port moves one
     * `hoveredIndex` with both the mouse and the keyboard, so it records which of the two last moved
     * it — otherwise either the ring appears under the mouse pointer, or never appears at all (what
     * it did before).
     */
    const [movedByKeyboard, setMovedByKeyboard] = React.useState(false);
    /* Focusable's `focus` state on the trigger, as a class — see TextField's clear button. */
    const [triggerFocused, setTriggerFocused] = React.useState(false);
    React.useEffect(() => {
        if (shown) {
            setHoveredIndex(selectedIndex);
        }
    }, [shown, selectedIndex]);
    /* `CoreClickOutside` + `onEscPress` upstream. */
    React.useEffect(() => {
        if (!isExpanded) {
            return undefined;
        }
        const onPointerDown = (e) => {
            if (!rootRef.current?.contains(e.target)) {
                setIsExpanded(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsExpanded(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isExpanded]);
    const isSelectable = (option) => 
    /* `modifyOptions` in Dropdown/helpers.js: an `unavailable` option is selectable unless it
       says otherwise, so that the click handler still fires for it. */
    option.unavailable && option.isSelectable === undefined ? true : option.isSelectable;
    const select = (option) => {
        setIsExpanded(false);
        onChange?.(option);
    };
    /*
     * `useMenuPatternKeys`, as far as this component needs it. Its `handleMenuKeydown` answers
     * Space/Enter (select the focused option), Escape/Tab (close), Home/End and the arrows; the
     * trigger's handler opens on Space/Enter/ArrowDown. Only the arrows were ported here, which left
     * no keyboard path to `select` at all: the browser's synthetic click on Space/Enter merely
     * toggled the menu shut again, and every option is `tabIndex={-1}` so none could be activated
     * directly.
     */
    const moveFocus = (to) => {
        setMovedByKeyboard(true);
        setHoveredIndex(to);
    };
    const selectFocused = () => {
        const option = selectableOptions[hoveredIndex === -1 ? selectedIndex : hoveredIndex];
        if (option) {
            select(option);
        }
    };
    const onTriggerKeyDown = (e) => {
        const count = selectableOptions.length;
        const from = hoveredIndex === -1 ? selectedIndex : hoveredIndex;
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isExpanded) {
                    selectFocused();
                }
                else {
                    setIsExpanded(true);
                    moveFocus(from === -1 ? 0 : from);
                }
                break;
            case 'ArrowDown':
            case 'ArrowUp': {
                e.preventDefault();
                if (!isExpanded) {
                    setIsExpanded(true);
                    moveFocus(from === -1 ? 0 : from);
                    break;
                }
                /* Alt+ArrowUp is upstream's "commit and close". */
                if (e.key === 'ArrowUp' && e.altKey) {
                    selectFocused();
                    setIsExpanded(false);
                    break;
                }
                if (count) {
                    moveFocus((from + (e.key === 'ArrowDown' ? 1 : -1) + count) % count);
                }
                break;
            }
            case 'Home':
                if (isExpanded && count) {
                    e.preventDefault();
                    moveFocus(0);
                }
                break;
            case 'End':
                if (isExpanded && count) {
                    e.preventDefault();
                    moveFocus(count - 1);
                }
                break;
            case 'Escape':
                if (isExpanded) {
                    setIsExpanded(false);
                }
                break;
            case 'Tab':
                /* Tab closes it, as `handleMenuKeydown` does. No `preventDefault`: focus SHOULD move on to
                   the next control — the options are not tabbable, so leaving the menu open would strand it
                   open behind whatever gains focus. */
                if (isExpanded) {
                    setIsExpanded(false);
                }
                break;
            default:
                break;
        }
    };
    const content = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.fdContent },
            React.createElement("div", { "data-part": "textContent", className: styles.textContent },
                label != null ? (React.createElement("span", { "data-part": "label", className: styles.label }, label)) : null,
                React.createElement("div", { "data-part": "selectedValue", "data-hook": FD_HOOKS.baseSelectedValue, className: styles.selectedValue }, selectedOption?.value)),
            React.createElement(ChevronDown, { "aria-hidden": true, "data-part": "arrowIcon", className: styles.arrowIcon })),
        mobile ? (React.createElement("select", { value: value ?? '', "data-part": "nativeSelect", "data-hook": FD_HOOKS.nativeSelect, "aria-label": ariaLabel, onChange: (e) => {
                const next = options.find((option) => option.id === e.target.value);
                if (next) {
                    onChange?.(next);
                }
            } }, options.map((option) => option.divider ? null : (React.createElement("option", { value: option.id, disabled: !isSelectable(option), key: option.id }, option.value))))) : null));
    const base = (React.createElement("div", { className: classNames(styles.fdBaseRoot, styles.floatingDropdownBase, {
            [styles.fdBaseMobile]: mobile,
        }, styles.fdBaseOverrideStyleParams), "data-part": "floatingDropdownBase", "data-hook": FD_HOOKS.base }, mobile ? (content) : (React.createElement("button", { type: "button", "aria-haspopup": "true", "aria-expanded": isExpanded, "aria-label": ariaLabel, "aria-controls": containerId, onFocus: () => setTriggerFocused(true), onBlur: () => setTriggerFocused(false), "data-part": "btn", "data-hook": FD_HOOKS.floatingDropdownButton, "data-priority": "secondary", className: classNames(styles.coreBtnRoot, styles.textButtonRoot, styles['tbPriority-secondary'], 
        /* `TextButton` takes `mobile` from TPAComponentsConsumer and writes it as a state, so
           the trigger needs it too — it selects WUT's mobile font column. No `hoverStyle`,
           deliberately: TextButton's defaultProps set only `priority` and FloatingDropdownBase
           passes none, which is why every `.tbHoverStyle-*` rule here is unreachable. */
        { [styles.tbMobile]: mobile, [styles.focusableFocus]: triggerFocused }, styles.btn) },
        React.createElement("span", { className: styles.btnContent }, content)))));
    return (React.createElement("div", { ref: rootRef, "data-mobile": mobile, "data-hook": dataHook, className: classNames(styles.root, styles.overrideStyleParams, 
        /* `displayInline` unconditionally — it was `!displayBlock`, and `displayBlock` is never
           passed. Keeping it matters: `.dropdownRoot:not(.displayBlock)` sets `inline-block`, and
           losing it cost the mobile sort selector 4px of height. */
        styles.displayInline, className) }, mobile ? (base) : (React.createElement("div", { "data-part": "dropdown", "data-hook": FD_HOOKS.coreDropdown, "data-mobile": mobile, className: classNames(styles.popoverRoot, styles.coreDropdownRoot, styles.dropdown, {
            [styles.contentVisible]: shown,
        }) },
        React.createElement("div", { ref: elementRef, className: styles.popoverElement, "data-hook": CORE_HOOKS.popoverElement, onClick: () => {
                /* Only the pointer gets here (`onTriggerKeyDown` prevents the keys' synthetic
                   click), so a mouse reopen must not keep the last keyboard ring. */
                setMovedByKeyboard(false);
                setIsExpanded((expanded) => !expanded);
            }, onKeyDown: onTriggerKeyDown }, base),
        shown ? (React.createElement("div", { "data-hook": CORE_HOOKS.popoverContent, "data-placement": "bottom-start", className: classNames(styles.popover, styles.dropdownContainer, styles.popoverContent), style: {
                position: 'absolute',
                left: 0,
                top: 0,
                zIndex: 1000,
                transform: `translate3d(-1px, ${elementRef.current?.offsetHeight ?? 0}px, 0)`,
            } },
            React.createElement("div", null,
                React.createElement("div", { "data-part": "dropdownContent", "data-hook": CORE_HOOKS.dropdownContent, className: classNames(styles.coreContentRoot, styles.coreDropdownContent) },
                    React.createElement("div", { role: "menu", id: containerId, tabIndex: -1, "data-hook": CORE_HOOKS.optionsContainer, className: styles.optionsContainer }, options.map((option, index) => {
                        if (option.divider) {
                            return (React.createElement("div", { key: `divider-${index}`, className: classNames(styles.coreOptionRoot, styles.optionDivider) }));
                        }
                        const optionIndex = selectableOptions.indexOf(option);
                        const selectable = isSelectable(option);
                        const optionDomId = createOptionId(containerId, option.id);
                        const hovered = !option.isDisabled && hoveredIndex === optionIndex;
                        const selected = !option.isDisabled && option.id === value;
                        return (React.createElement("div", { key: option.id, id: optionDomId, tabIndex: -1, role: "menuitem", title: option.value, "aria-labelledby": createOptionLabelId(optionDomId), "aria-disabled": !selectable || option.isDisabled || !!option.unavailable, "data-hook": CORE_HOOKS.dropdownContentOption, className: classNames(styles.coreOptionRoot, styles.coreContentDropdownOption, {
                                [styles.coreOptionSelectable]: selectable,
                                [styles.coreOptionDisabled]: option.isDisabled,
                                [styles.coreOptionHovered]: hovered,
                                [styles.coreOptionSelected]: selected,
                            }), onClick: selectable ? () => select(option) : undefined, onMouseEnter: selectable
                                ? () => {
                                    setMovedByKeyboard(false);
                                    setHoveredIndex(optionIndex);
                                }
                                : undefined, onMouseLeave: () => setHoveredIndex(-1) },
                            React.createElement("div", { "data-part": "option", "data-hook": DROPDOWN_OPTION_HOOK, className: classNames(styles.optionRoot, styles.option, { [styles.optionIsFocused]: hovered && movedByKeyboard }, 
                                /* FloatingDropdown passes `textOverflow: wrap`. */
                                styles['optionTextOverflow-wrap'], {
                                    [styles.optionSelectable]: selectable,
                                    [styles.optionUnavailable]: !!option.unavailable,
                                }) },
                                React.createElement("div", { className: styles.optionContentWrapper },
                                    React.createElement("span", { id: createOptionLabelId(optionDomId), "aria-disabled": !(selectable && !option.unavailable), "data-part": "title", className: classNames(styles.textRoot, styles['textTypography-runningText'], 
                                        /* `Text` emits its own `mobile` state from the same context. */
                                        { [styles.textMobile]: mobile }, styles['textPriority-primary'], styles.title) }, option.value)))));
                    })))))) : null))));
};
//# sourceMappingURL=FloatingDropdown.js.map